import type { ReceiptDiscrepancy } from "../../api-models";

/**
 * What a discrepancy has actually settled — the mirror of coveredQty in discrepancy.go,
 * and it must stay a mirror: the server is the only authority on closure, so any screen
 * that computes this differently will contradict it.
 *
 * REMAINING_DELIVERY is excluded deliberately. It documents that a shortfall is still
 * expected from the source, which is a note about the future rather than a resolution.
 * Counting it shows a dispute as fully accounted for while the server still holds it open,
 * which leaves a reader unable to explain why it is OPEN and — because the disposition
 * form caps its quantity at the outstanding balance — with no room to record the very
 * disposition that would close it.
 */
export function coveredQty(d: ReceiptDiscrepancy): number {
  return (d.dispositions ?? []).reduce((sum, x) => sum + (x.type === "REMAINING_DELIVERY" ? 0 : x.qty), 0);
}

/** Whatever remains unsettled: what the closure gate is still waiting on. */
export function outstandingQty(d: ReceiptDiscrepancy): number {
  return d.dispatchedQty - coveredQty(d);
}

/**
 * Quantity flagged as still expected. Reported separately from coverage so it is never
 * mistaken for a settlement — it is a promise, not an outcome.
 */
export function expectedQty(d: ReceiptDiscrepancy): number {
  return (d.dispositions ?? []).reduce((sum, x) => sum + (x.type === "REMAINING_DELIVERY" ? x.qty : 0), 0);
}
